import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { DeltaUnit, MetricDelta, MetricDeltaSecondary } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { formatNumber } from 'utils/formatters';

import { formatCurrencyValue, NO_VALUE } from '../formatMetric';
import { visuallyHidden } from '../visuallyHidden';

export interface IPeriodDeltaProps extends MetricDelta {
  /** Symbol or ISO code, meaningful only for the `currency` unit. */
  currency?: string;
  /** Arrow and magnitude only — the secondary figure belongs to the tooltip. */
  compact?: boolean;
  className?: string;
}

type Direction = MetricDelta['direction'];
type Sentiment = MetricDelta['sentiment'];

const DIRECTION_GLYPHS: Record<Direction, string> = {
  up: '↑',
  down: '↓',
  flat: '→'
};

const DIRECTION_LABEL_KEYS: Record<Direction, string> = {
  up: 'Increased',
  down: 'Decreased',
  flat: 'Unchanged'
};

const PERCENTAGE_POINTS_KEY = 'PercentagePoints';

const UNIT_SUFFIX_KEYS: Partial<Record<DeltaUnit, string>> = {
  pp: PERCENTAGE_POINTS_KEY,
  points: 'Points'
};

// Business meaning, not direction: a rise in not-eligible customers takes negative treatment
// (FR § Period comparison display).
const SENTIMENT_COLORS: Record<Sentiment, string> = {
  positive: tokens.color.positive,
  negative: tokens.color.negative,
  neutral: tokens.color['text-secondary']
};

// Colour is the only visual channel carrying the good/bad valence, so assistive tech is given the
// valence in words (WCAG 1.4.1). `neutral` has nothing to say and stays silent.
const SENTIMENT_LABEL_KEYS: Partial<Record<Sentiment, string>> = {
  positive: 'Favourable',
  negative: 'Unfavourable'
};

// Only counts and currency carry the FR's optional second figure. A `pp` primary is already the
// percentage-point movement, so a second one beside it would state the same quantity twice.
// PLA-8815.
const UNITS_WITH_SECONDARY: DeltaUnit[] = ['count', 'currency'];

// `pp` prints the per-cent AND the percentage-points suffix, unsigned, on both directions:
// `589:27760` reads `↑ $800 (6.2% pp)` and `589:27903` reads `↓ 1,250 (3.1% pp)`. `percent` prints
// the per-cent alone — the catalogue's form for a count KPI's relative movement
// (`Total Registered Users: 125,000 (+4.2%)`, O-COM-001). PLA-8815.
const SECONDARY_SUFFIX_KEYS: Record<MetricDeltaSecondary['unit'], string | undefined> = {
  pp: PERCENTAGE_POINTS_KEY,
  percent: undefined
};

const PeriodDeltaWrapper = styled.span<{ $sentiment: Sentiment }>`
  display: inline-flex;
  align-items: baseline;
  color: ${({ $sentiment }) => SENTIMENT_COLORS[$sentiment]};
  font-size: ${tokens.type['metric-delta-size']};
  font-weight: ${tokens.type['metric-delta-weight']};
  line-height: ${tokens.type['metric-delta-line']};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: ${tokens.space['4']};
  }
`;

const SentimentStatus = styled.span`
  ${visuallyHidden}
`;

const PeriodDelta: React.FC<IPeriodDeltaProps> = ({
  direction,
  value,
  unit,
  sentiment,
  secondary,
  currency,
  compact,
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  // The arrow carries the sign, so both figures render as magnitudes — a payload that also signs
  // its own numbers would otherwise read `↓ -1,250`.
  const magnitude = Math.abs(value);
  const suffixKey = UNIT_SUFFIX_KEYS[unit];
  const amount =
    unit === 'currency' ? formatCurrencyValue(magnitude, currency) : formatNumber(magnitude);
  const primary = suffixKey ? `${amount} ${t(`${TCustomerInsights}.Delta.${suffixKey}`)}` : amount;

  // `formatCurrencyValue` degrades to NO_VALUE while the tenant currency is unresolved, which is
  // every render before `useDefaultCurrency` settles and every render on a tenant without one. The
  // arrow, the sentiment word, its hue and the secondary figure each assert a sized, valenced
  // movement, so leaving them beside a magnitude the same chip says it cannot state reads as
  // `↑ — (6.2% pp)` — a degraded readout prints no digit (FR § Data states).
  const isMagnitudeUnavailable = amount === NO_VALUE;

  const showSecondary =
    !compact &&
    !isMagnitudeUnavailable &&
    secondary !== undefined &&
    UNITS_WITH_SECONDARY.indexOf(unit) !== -1;
  const secondarySuffixKey = secondary && SECONDARY_SUFFIX_KEYS[secondary.unit];
  const secondaryText = showSecondary
    ? `(${formatNumber(Math.abs(secondary.value))}%${
        secondarySuffixKey ? ` ${t(`${TCustomerInsights}.Delta.${secondarySuffixKey}`)}` : ''
      })`
    : '';

  const renderedSentiment: Sentiment = isMagnitudeUnavailable ? 'neutral' : sentiment;
  const sentimentKey = SENTIMENT_LABEL_KEYS[renderedSentiment];

  return (
    <PeriodDeltaWrapper
      className={className}
      $sentiment={renderedSentiment}
      data-testid="period-delta"
      // The degraded readout withholds the arrow and the valence, so it withholds the machine-
      // readable copy of them too: a consumer styling off `[data-direction]` would otherwise
      // re-assert a movement this chip has just refused to state.
      data-direction={isMagnitudeUnavailable ? undefined : direction}
      data-sentiment={renderedSentiment}
    >
      {isMagnitudeUnavailable ? null : (
        <span
          role="img"
          aria-label={t(`${TCustomerInsights}.Delta.${DIRECTION_LABEL_KEYS[direction]}`)}
        >
          {DIRECTION_GLYPHS[direction]}
        </span>
      )}
      {sentimentKey ? (
        <SentimentStatus data-testid="period-delta-sentiment-status">
          {t(`${TCustomerInsights}.Delta.${sentimentKey}`)}
        </SentimentStatus>
      ) : null}
      <span data-testid="period-delta-magnitude">{primary}</span>
      {secondaryText ? <span data-testid="period-delta-relative">{secondaryText}</span> : null}
    </PeriodDeltaWrapper>
  );
};

export default PeriodDelta;
